import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Controls trade bar resizing (extend/shorten) in Plantafel vertical month view
export default class extends Controller {
  static targets = ["leftHandle", "rightHandle"]
  static values = {
    taskId: Number,
    tradeId: Number,
    taskTradeId: Number,
    startDate: String,
    endDate: String,
    viewMode: String,
  }

  connect() {
    this.isResizing = false
    this.resizeDirection = null
    this.startX = 0
    this.initialWidth = 0
    this.initialLeft = 0
    this.columnWidth = 0
    this.currentDaysChange = 0

    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleMouseUp = this.handleMouseUp.bind(this)
  }

  disconnect() {
    document.removeEventListener("mousemove", this.handleMouseMove)
    document.removeEventListener("mouseup", this.handleMouseUp)
  }

  startResizeLeft(event) {
    event.preventDefault()
    event.stopPropagation()
    this.startResize(event, "left")
  }

  startResizeRight(event) {
    event.preventDefault()
    event.stopPropagation()
    this.startResize(event, "right")
  }

  // Prevent click events on resize handles from opening the drawer
  handleClick(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  startResize(event, direction) {
    this.isResizing = true
    this.resizeDirection = direction
    this.startX = event.clientX
    this.currentDaysChange = 0
    this.lastSnappedDays = 0

    // Calculate column width based on the grid
    this.calculateColumnWidth()

    // Store original dates
    this.originalStartDate = this.startDateValue
    this.originalEndDate = this.endDateValue

    // Store initial bar dimensions
    this.initialWidth = this.element.offsetWidth
    this.initialLeft = this.element.offsetLeft

    // Add visual feedback
    this.element.classList.add("ring-2", "ring-bautask-green/50")
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"

    document.addEventListener("mousemove", this.handleMouseMove)
    document.addEventListener("mouseup", this.handleMouseUp)
  }

  handleMouseMove(event) {
    if (!this.isResizing) return

    const deltaX = event.clientX - this.startX
    const snappedDaysChange = Math.round(deltaX / this.columnWidth)

    // Only update if we've crossed a day boundary
    if (snappedDaysChange === this.lastSnappedDays) return
    this.lastSnappedDays = snappedDaysChange
    this.currentDaysChange = snappedDaysChange

    // Calculate new dates
    const { newStartDate, newEndDate } = this.calculateNewDates(snappedDaysChange)

    // Validate dates - must be at least 1 day
    if (newStartDate > newEndDate) return

    // Update visual preview
    this.updateBarPreview(snappedDaysChange)
  }

  handleMouseUp(_event) {
    if (!this.isResizing) return

    const daysChange = this.currentDaysChange

    // Remove visual feedback
    this.element.classList.remove("ring-2", "ring-bautask-green/50")
    document.body.style.cursor = ""
    document.body.style.userSelect = ""

    document.removeEventListener("mousemove", this.handleMouseMove)
    document.removeEventListener("mouseup", this.handleMouseUp)

    this.isResizing = false

    if (daysChange !== 0) {
      const { newStartDate, newEndDate } = this.calculateNewDates(daysChange)

      // Validate dates
      if (newStartDate <= newEndDate) {
        this.saveNewDates(newStartDate, newEndDate)
      } else {
        this.resetBarPosition()
      }
    }
  }

  calculateColumnWidth() {
    // Find the grid container to calculate column width
    const gridContainer = this.findGridContainer()

    if (gridContainer) {
      const gridCells = gridContainer.querySelectorAll(":scope > div")
      if (gridCells.length > 0) {
        const totalWidth = gridContainer.offsetWidth
        const numColumns = gridCells.length
        this.columnWidth = totalWidth / numColumns
        return
      }
    }

    // Fallback: estimate based on parent container
    const container = this.element.closest(".section-row") || this.element.parentElement
    const containerWidth = container ? container.offsetWidth : 800

    // For vertical_month view, estimate 31 columns
    this.columnWidth = containerWidth / 31
  }

  findGridContainer() {
    // Try to find the background grid in the section
    const sectionRow = this.element.closest(".section-row")
    if (sectionRow) {
      const grid = sectionRow.querySelector(".grid.pointer-events-none")
      if (grid) return grid
    }

    return null
  }

  calculateNewDates(daysChange) {
    const startDate = new Date(this.originalStartDate)
    const endDate = new Date(this.originalEndDate)

    const newStartDate = new Date(startDate)
    const newEndDate = new Date(endDate)

    if (this.resizeDirection === "left") {
      newStartDate.setDate(startDate.getDate() + daysChange)
    } else {
      newEndDate.setDate(endDate.getDate() + daysChange)
    }

    return { newStartDate, newEndDate }
  }

  updateBarPreview(daysChange) {
    const snappedPixelChange = daysChange * this.columnWidth
    const minWidth = this.columnWidth * 0.5

    if (this.resizeDirection === "left") {
      const newLeft = this.initialLeft + snappedPixelChange
      const newWidth = this.initialWidth - snappedPixelChange

      if (newWidth >= minWidth) {
        this.element.style.left = `${newLeft}px`
        this.element.style.width = `${newWidth}px`
      }
    } else {
      const newWidth = this.initialWidth + snappedPixelChange

      if (newWidth >= minWidth) {
        this.element.style.width = `${newWidth}px`
      }
    }
  }

  resetBarPosition() {
    this.element.style.left = ""
    this.element.style.width = ""
  }

  async saveNewDates(newStartDate, newEndDate) {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    // Add loading state
    this.element.style.opacity = "0.6"
    this.element.style.pointerEvents = "none"

    try {
      const startDateStr = this.formatDateForApi(newStartDate)
      const endDateStr = this.formatDateForApi(newEndDate)

      const response = await fetch(
        `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/schedule`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/vnd.turbo-stream.html",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            task_trade: {
              start_date: startDateStr,
              end_date: endDateStr,
            },
            view_mode: this.viewModeValue,
            view_start_date: this.getViewStartDate(),
          }),
        },
      )

      const contentType = response.headers.get("Content-Type") || ""
      const html = await response.text()

      // Check if response contains a warning modal (assignments outside new range)
      if (html.includes("plantafel-trade-shorten-warning-modal")) {
        // Reset bar position since user needs to confirm
        this.resetBarPosition()
        this.element.style.opacity = ""
        this.element.style.pointerEvents = ""
        // Show the warning modal
        Turbo.renderStreamMessage(html)
        return
      }

      if (response.ok) {
        if (contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
        } else {
          // Reload the page to show updated data
          window.location.reload()
        }
      } else {
        Honeybadger.notify("Failed to update trade schedule", {
          context: { level: "error", component: "plantafel-trade-resize", action: "save_dates" },
        })
        this.resetBarPosition()
        this.element.style.opacity = ""
        this.element.style.pointerEvents = ""
        this.showErrorNotification("Fehler beim Speichern der Änderungen")
      }
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-trade-resize", action: "save_dates" },
      })
      this.resetBarPosition()
      this.element.style.opacity = ""
      this.element.style.pointerEvents = ""
      this.showErrorNotification("Fehler beim Speichern der Änderungen")
    }
  }

  getViewStartDate() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get("date") || this.formatDateForApi(new Date())
  }

  formatDateForApi(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  showErrorNotification(message) {
    const notification = document.createElement("div")
    notification.className =
      "fixed top-4 right-4 z-100 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg"
    notification.innerHTML = `
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      </div>
    `
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }
}
